/*
Componente ListaForumMinhasMensagens
Este componente é responsável por exibir uma lista de mensagens do fórum relacionadas ao usuário logado. 
Ele utiliza o contexto de autenticação para obter os dados do usuário e faz uma requisição à API para buscar as mensagens correspondentes.

O componente é referenciado na configuração de rotas em src/routes.js, permitindo que seja acessado através da URL "/listagem-forum-minhas-mensagens".

Ele encapsula a lógica de exibição das mensagens, incluindo a renderização de cada mensagem em um card, com informações como assunto, comentário, data e status. Além disso, permite a navegação para detalhes da mensagem ou ações adicionais, dependendo do tipo de usuário logado.
O componente também lida com estados de carregamento e erros, garantindo uma experiência de usuário adequada durante a busca das mensagens. 


 * O componente usa o hook useEffect para buscar as mensagens assim que for montado, e o hook useState para armazenar a lista de mensagens no estado do componente.
 * O hook useNavigate é usado para redirecionar o usuário para a página de resultado em caso de erro.
 * A URL da requisição para buscar as mensagens é construída usando a variável de ambiente REACT_APP_API_URL, garantindo que a aplicação possa ser configurada para diferentes ambientes (desenvolvimento, produção, etc.) sem precisar alterar o código.
 *  Estilo básico usando classes do Bootstrap para a lista de mensagens.
 
*/
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CampoTexto from "../CampoTexto";
import { useAuth } from "../../contextos/AuthContext"; // Importa o hook useAuth do contexto de autenticação para acessar o estado do usuário logado e funções relacionadas à autenticação
import Botao from "../Botao";
import CadastroForum from "../CadastroForum";


const ListaForumMinhasMensagens = () => {
    
    const { token } = useAuth(); // reage automaticamente ao login!
    const location = useLocation(); // Hook para acessar o estado passado pela rota, que pode conter os dados do usuário logado ou outros dados relevantes para a exibição do fórum
    const navigate = useNavigate(); // Hook para redirecionar o usuário para outras páginas, como a página de resultado em caso de erro
    //Use o operador de coalescência nula (??). Ele só assume o valor padrão se o dado for estritamente null ou undefined, permitindo que a string vazia passe.
    const [mensagens, setMensagens] = useState([]); // Estado para armazenar a lista de mensagens, inicialmente vazio
    const [carregando, setCarregando] = useState(true); // Estado para controlar o carregamento dos dados, inicialmente verdadeiro
    const [mensagensFiltradas, setMensagensFiltradas] = useState([]); // Estado para armazenar a lista de mensagens filtradas, inicialmente vazio
    const [termoBusca, setTermoBusca] = useState(''); // Estado para armazenar o termo de busca digitado pelo usuário

    // O estado do Modal vive aqui no "pai" de todos. Controla se o modal de cadastro do fórum está aberto ou fechado
    const [modalCadastroForumAberto, setModalCadastroForumAberto] = useState(false);
    
    const [mensagemSelecionada, setMensagemSelecionada] = useState(null); // Estado para armazenar a mensagem selecionada para edição, inicialmente null



/* Esta função roda uma vez quando o componente é montado
/* Carrega a lista de mensagens do backend assim que o componente for exibido, garantindo que tenhamos os dados para mostrar na tela */    
useEffect(() => {

    // Criamos um controlador de abort para permitir o cancelamento da requisição caso o componente seja desmontado antes da conclusão da requisição, evitando erros de memória e chamadas desnecessárias ao backend
    const controller = new AbortController();

    //Criamos uma função assíncrona para buscar as mensagens do backend, incluindo o token de autenticação no cabeçalho da requisição. Tem que ser criada dentro do useEffect porque o useEffect não pode ser assíncrono diretamente, mas pode chamar funções assíncronas. 
    const buscarMensagens = async () => {
        
        try{
            // Faz a requisição para o backend para buscar as mensagens, incluindo o token no cabeçalho de autorização
            
            //Usa a URL da sua variável de ambiente e completa a URL da requisição para buscar as mensagens, incluindo o status de filtro como parâmetro de consulta. O backend pode então usar esse parâmetro para filtrar as mensagens de acordo com o status desejado (enviado, liberado, bloqueado, etc.). 
            const url = `${process.env.REACT_APP_API_URL}/forum-list-minhas-msg`; 

            const resposta = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    // 3. Envia o token via Header Authorization
                    'Authorization': `Bearer ${token}`
                },
                signal: controller.signal // ✅ Vincula o abort ao fetch
            });

            if (!resposta.ok) {
                    // Se o backend retornar 401 ou 403, algo está errado com a permissão
                    throw new Error(`Erro na requisição: ${resposta.status}`);
                }
            
            const dadosRetorno = await resposta.json();
        
            if (dadosRetorno.status === 'sucesso') {
            // Em React, em vez de chamar uma função 'listarUsuarios', 
            // nós salvamos os dados no estado (useState)

            setMensagens(dadosRetorno.dados); // Salva as mensagens no estado para que os dados originalmente recebidos do backend estejam disponíveis para renderização e outras operações
            setMensagensFiltradas(dadosRetorno.dados); // Inicialmente, a lista filtrada é a mesma que a lista completa, mas manteremos essa separação para facilitar a implementação da funcionalidade de busca/filtro posteriormente
            setCarregando(false); // Define o estado de carregamento como falso após receber os dados
            console.log("Mensagens recebidas do backend:", dadosRetorno.dados); // Log para verificar os dados recebidos do backend

            } else {
                navigate('/resultado', { 
                    state: { 
                    status: dadosRetorno.status,
                    mensagem: dadosRetorno.mensagem
                          }
                    });   
            }
        }catch(error){

            if (error.name === 'AbortError') return; // ✅ 1ª execução cancelada silenciosamente
            navigate('/resultado', { 
                    state: { 
                    status: "erro",
                    mensagem: "Ocorreu um erro ao buscar as mensagens: " + error.message
                          }
                    });
        }
    }

    
    buscarMensagens(); // Chama a função para buscar as mensagens quando o componente for montado

    return () => controller.abort(); // ✅ Cancela quando desmonta (1ª execução do StrictMode)
    



}, []);


/* Função para filtrar as mensagens com base no termo de busca digitado pelo usuário, atualizando a lista de mensagens exibida na tela conforme o termo de busca é digitado. A função verifica se o termo de busca está presente no título ou na descrição das mensagens, e atualiza a lista filtrada de acordo. Se o campo de busca for limpo, a lista original é restaurada. */
const filtrarPostagens = (termo) => {
    setTermoBusca(termo); // Atualiza o estado do termo de busca para refletir o valor digitado pelo usuário no campo de busca
    if (termo.trim() === "") {
        setMensagensFiltradas(mensagens); // Se limpar, volta a lista original
        return;
    }
    // Filtra as mensagens com base no termo de busca, verificando se o termo está presente no assunto (case-insensitive)
    const resultado = mensagens.filter((mensagem) => {
        return (
            mensagem.assunto.toLowerCase().includes(termo.toLowerCase())
        );
    });


    setMensagensFiltradas(resultado); // AQUI é onde a mágica acontece na tela  
    console.log("Mensagens filtradas:", resultado); // Log para verificar as mensagens filtradas com base no termo de busca
}

// caso os dados do backend ainda estejam sendo carregados, exibe um spinner de carregamento para informar ao usuário que os dados estão sendo processados. Isso melhora a experiência do usuário, evitando que ele veja uma tela em branco ou incompleta enquanto os dados são carregados.
if (carregando) {
    return (
        <div className="container-fluid text-center mt-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Carregando...</span>
            </div>
        </div>
    );
}



// Renderiza a lista de mensagens do fórum, exibindo o título e a descrição de cada mensagem. Se não houver mensagens para exibir, mostra uma mensagem indicando que nenhum tópico foi encontrado. O campo de busca é exibido acima da lista de mensagens, permitindo ao usuário filtrar os tópicos com base no termo digitado.


/* O código abaixo monta a estrutura de tópicos do fórum com as mensagens do usuário logado */
    return (
        <div className="container-fluid justify-content-center">
            <h1 className="h1">Minhas Mensagens</h1>

            {token ? (
                    
               <>
                <div className="container-fluid mb-3 bg-dark-subtle p-3 rounded ">
                    <CampoTexto
                        label={''}
                        wrapperClass={'w-100'}
                        className={'w-100 bg-success-subtle p-2 rounded fs-5 fw-bold'}
                        placeholder={'Digite a palavra-chave para buscar um tópico...'}
                        valor={termoBusca} // Você pode implementar a lógica de busca e armazenar o valor do campo de busca no estado, usando useState, e passar esse valor aqui para controlar o campo de texto
                        aoDigitado={(valor) => filtrarPostagens(valor)} // Você pode implementar a lógica de busca e passar uma função aqui que atualize o estado com o valor do campo de busca, e depois use esse valor para filtrar a lista de postagens exibida na tela
                        editavel={true}
                    >
                    </CampoTexto> 

                        {/* Renderiza a lista de mensagens filtradas, exibindo o título e a descrição de cada mensagem. Se não houver mensagens filtradas para exibir, mostra uma mensagem indicando que nenhum tópico foi encontrado. A renderização da lista de mensagens é feita com base na lista de mensagens filtradas, que é atualizada conforme o usuário digita no campo de busca. */}
                        {mensagensFiltradas.length === 0 && (
                            <div className="container-fluid justify-content-center">
                                <p className="text-start fs-3">Nenhum tópico encontrado.</p>
                            </div>
                        )}

                        {console.log("Renderizando mensagens filtradas:", mensagensFiltradas)} {/* Log para verificar as mensagens filtradas que estão sendo renderizadas na tela */}
                        {mensagensFiltradas.map((comentario) => {
                            // Extrai o nome antes do @ do email
                            const nomeUsuario = (comentario.usuario || '').split('@')[0]; // Extrai o nome antes do @ do email

                            
                            return (
                                <div key={comentario.uuid} className="container-fluid d-flex m-3 mt-3 bg-light p-3 rounded border-success-subtle">
                                    
                                    <div className="d-flex flex-column justify-content-center align-items-center p-3 border-end border-success-subtle fs-3">
                                        <div>
                                            {comentario.status === 'publicado' ?(
                                                <span className="mb-1 badge text-bg-primary">
                                                    {comentario.status}
                                                </span>    
                                            ):
                                            (
                                                <>
                                                    <span className="mb-1 badge text-bg-warning">
                                                        {comentario.status}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {/* Lógica de permissão de edição 
                                        Arrow function que calcula se o comentário pode ser editado com base no status e no tempo decorrido desde a postagem. Se o comentário estiver dentro do período permitido para edição (30 minutos após o envio) e tiver o status 'enviado', o botão de edição será exibido. Caso contrário, o botão não será renderizado. */}
                                        {/* A função calcula o tempo em minutos desde que o comentário foi enviado, usando a diferença entre a data atual e a data do comentário. Se o tempo decorrido estiver dentro do intervalo permitido para edição, o botão de edição será exibido. Caso contrário, o botão não será renderizado. */}
                                        
                                        {(() => {
                                                // Calcula o tempo em minutos desde que o comentário foi enviado. Date é usado para criar objetos de data a partir das strings de data, e a diferença entre as datas é convertida para minutos. Isso permite determinar se o comentário ainda está dentro do período permitido para edição (30 minutos após o envio).
                                                const minutosPassados = (new Date() - new Date(comentario.data)) / (1000 * 60);
                                                const podeEditar = comentario.status === 'enviado' && minutosPassados >= 0 && minutosPassados <= 30;

                                                return podeEditar && (
                                                    <>
                                                        <Botao
                                                        //Ao clicar no botão, abre o modal de cadastro de novo tópico, setando o estado para true
                                                            aoClicar={() => {    
                                                                // Passa a mensagem selecionada para o estado do componente pai, permitindo que o modal de cadastro de novo tópico seja preenchido com os dados da mensagem selecionada para edição
                                                            setMensagemSelecionada({
                                                                    id: comentario.id,
                                                                    topico: comentario.assunto,
                                                                    texto: comentario.comentario
                                                            }); // Passa a mensagem selecionada para o estado do componente pai, permitindo que o modal de cadastro de novo tópico seja preenchido com os dados da mensagem selecionada para edição
                                                                setModalCadastroForumAberto(true);
                                                            }} // Redireciona para a página de cadastro de novo tópico
                                                            className="btn btn-outline-secondary btn-lg mb-3"
                                                        >
                                                            Editar   

                                                        </Botao>
                                                    </>
                                                    
                                                );
                                            })()}    

                                        </div>        
                                    
                                    <div className="d-flex flex-column ms-3">
                                        <h2 className="h2">{comentario.assunto}</h2>
                                        
                                        <p className="text-start fs-5 text-break" style={{ whiteSpace: 'pre-line' }}>{comentario.comentario}</p>
                                        <div className="d-flex flex-column ms-3">
                                            <small className="fw-bolder">      
                                                {comentario.data}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
            
                </div>
                </>     

            ) : (
                <div className="container-fluid justify-content-center">
                    <p className="text-start fs-3">Você precisa estar logado para acessar esta página.</p>
                </div>
            )}


            <CadastroForum
                /*Importante - ao passar estes parâmetros para o componente CadastroForum estamos elevando o estado destas variáveis para este componente pai ListaForum. Isto permite que o ListaForum controle a exibição do modal e reaja ao sucesso do Cadastro */
                // Controla a exibição do modal conforme o estado
                exibir={modalCadastroForumAberto} 
                aoFechar={() => setModalCadastroForumAberto(false)} 
                id={mensagemSelecionada?.id}
                topico={mensagemSelecionada?.topico}
                texto={mensagemSelecionada?.texto}
                
            />
        </div>
    );

}



export default ListaForumMinhasMensagens;